"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader2, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactInfo = {
  address: "Global Network HQ, Tech Corridor, San Francisco, CA 94105",
  phone: "+1 (800) TECH-N2N",
  email: "access@n2n-capital.com"
};
export default function ContactUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="14859" id="partner-with-us" ref={ref} className="relative w-full min-h-[800px] flex flex-col lg:flex-row overflow-hidden bg-background text-foreground" style={{
    "--background": "42 71% 97%",
    "--foreground": "24 10% 10%",
    "--card": "43 59% 90%",
    "--card-foreground": "24 10% 10%",
    "--primary": "42 71% 97%",
    "--primary-foreground": "24 10% 10%",
    "--secondary": "43 59% 90%",
    "--secondary-foreground": "24 10% 10%",
    "--accent": "26 91% 37%",
    "--accent-foreground": "42 71% 97%",
    "--border": "43 30% 80%",
    "--input": "43 30% 80%",
    "--ring": "26 91% 37%"
  } as React.CSSProperties}>
      
      <div className="relative w-full lg:w-1/2 bg-card text-card-foreground p-10 lg:p-20 flex flex-col justify-between overflow-hidden border-r border-border/50">
        
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
            <path d="M100,100 L300,150 L500,100 L700,200" fill="none" stroke="currentColor" strokeWidth="1" />
            <path d="M50,300 L250,350 L450,300 L650,400" fill="none" stroke="currentColor" strokeWidth="1" />
            <path d="M150,500 L350,550 L550,500 L750,600" fill="none" stroke="currentColor" strokeWidth="1" />
            <circle cx="300" cy="150" r="2" fill="currentColor" />
            <circle cx="500" cy="100" r="2" fill="currentColor" />
            <circle cx="250" cy="350" r="2" fill="currentColor" />
            <circle cx="550" cy="500" r="2" fill="currentColor" />
          </svg>
        </div>

        <div className={`relative z-10 transition-all duration-1000 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="text-4xl md:text-6xl font-sans font-bold mb-6 tracking-tighter uppercase text-foreground">
            Institutional Grade Partnership
          </h2>
          <p className="text-lg md:text-xl font-serif text-foreground/70 max-w-md leading-relaxed">
            Secure your position within our high-frequency intelligence network. 
            We provide the technical infrastructure and capital depth required for global scaling.
          </p>
        </div>

        <div className={`relative z-10 mt-12 space-y-8 transition-all duration-1000 delay-300 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="flex items-start gap-4 group">
            <div className="p-3 rounded-none bg-accent/5 border border-accent/20 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-500">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-sans font-semibold text-accent uppercase tracking-widest text-xs mb-1">Network Node</h3>
              <p className="text-foreground/60 max-w-xs font-serif">
                {contactInfo.address}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 group">
            <div className="p-3 rounded-none bg-accent/5 border border-accent/20 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-500">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-sans font-semibold text-accent uppercase tracking-widest text-xs mb-1">Direct Line</h3>
              <Link to={`tel:${contactInfo.phone}`} className="text-foreground/60 hover:text-accent transition-colors font-serif">
                {contactInfo.phone}
              </Link>
            </div>
          </div>

          <div className="flex items-start gap-4 group">
            <div className="p-3 rounded-none bg-accent/5 border border-accent/20 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-500">
              <Mail className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-sans font-semibold text-accent uppercase tracking-widest text-xs mb-1">Secure Protocol</h3>
              <Link to={`mailto:${contactInfo.email}`} className="text-foreground/60 hover:text-accent transition-colors font-serif">
                {contactInfo.email}
              </Link>
            </div>
          </div>
        </div>
      </div>

      
      <div className="w-full lg:w-1/2 bg-background p-10 lg:p-20 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-ring/5 via-transparent to-transparent pointer-events-none"></div>
        <div className="w-full max-w-lg relative z-10">
          <div className={`mb-10 transition-all duration-1000 delay-500 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <h3 className="text-3xl font-sans font-bold text-foreground mb-2 tracking-tight flex items-center">
              Network Support
              <span className="inline-block w-2 h-2 rounded-full bg-chart-2 ml-3 animate-pulse shadow-[0_0_8px_var(--chart-2)]" />
            </h3>
            <p className="text-muted-foreground font-serif">
              Our systems are live. Connect with our engineering team for immediate integration support.
            </p>
          </div>

          <FormProvider formKey="partner_with_us_form_n2ncapital" formId="partner_with_us_form_n2ncapital" sectionName="contact-us-section" defaultValues={{
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        }} validationRules={{
          name: {
            required: "Identity verification required"
          },
          email: {
            required: "Communication channel required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid protocol format"
            }
          },
          phone: {
            required: false
          },
          subject: {
            required: false
          },
          message: {
            required: "Payload required"
          }
        }} submitText="Initialize Connection" successMessage="Transmission received. Handshake complete." errorMessage="Network error. Please re-attempt connection.">
            <ContactFormContent isVisible={isVisible} />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ContactFormContent({
  isVisible
}: {
  isVisible: boolean;
}) {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center p-8 bg-card border border-accent/10 rounded-none shadow-lg animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-4 text-accent">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground mb-2">
          Handshake Complete
        </h3>
        <p className="text-muted-foreground mb-6 font-serif">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-accent text-accent hover:bg-accent/10 transition-all duration-300 rounded-none uppercase tracking-widest text-xs">
          New Transmission
        </Button>
      </div>;
  }
  const inputClasses = "bg-transparent border-0 border-b border-input rounded-none px-0 py-3 shadow-none focus-visible:ring-0 focus-visible:border-accent focus-visible:border-b-2 transition-all duration-300 placeholder:text-muted-foreground/30 font-serif";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className={`transition-all duration-700 delay-700 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans uppercase tracking-widest text-[10px] font-bold">
                Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="ID_VERIFY" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className={`transition-all duration-700 delay-[800ms] transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans uppercase tracking-widest text-[10px] font-bold">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="PROTOCOL_ADDR" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`transition-all duration-700 delay-[900ms] transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-sans uppercase tracking-widest text-[10px] font-bold">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="COMMS_INT" {...field} className={inputClasses} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-700 delay-[1000ms] transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <FormField control={control} name="subject" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-sans uppercase tracking-widest text-[10px] font-bold">
                  Subject
                </FormLabel>
                <FormControl>
                  <Input placeholder="QUERY_TYPE" {...field} className={inputClasses} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className={`transition-all duration-700 delay-[1100ms] transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans uppercase tracking-widest text-[10px] font-bold">
                Message
              </FormLabel>
              <FormControl>
                <Textarea placeholder="PAYLOAD_CONTENT" {...field} className={`${inputClasses} min-h-[120px] resize-none`} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-none text-xs font-mono animate-in fade-in slide-in-from-top-2">
          {submitError}
        </div>}

      <div className={`pt-4 transition-all duration-700 delay-[1200ms] transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-transparent border border-accent text-accent hover:bg-accent/5 hover:shadow-md font-sans font-bold uppercase tracking-widest text-sm rounded-none transition-all duration-500">
          {isSubmitting ? <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Initializing...
            </> : <>
              Initialize Connection
              <ArrowRight className="ml-2 h-4 w-4" />
            </>}
        </Button>
      </div>
    </Form>;
}